import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  SelectChangeEvent
} from '@mui/material';
import { HotelInfo, ExistingHotel } from '@features/deals/types';

interface HotelSelectionStepProps {
  hotelInfo: HotelInfo;
  setHotelInfo: React.Dispatch<React.SetStateAction<HotelInfo>>;
  existingHotels: ExistingHotel[];
  selectedHotelId: string;
  isNewHotel: boolean;
  handleHotelSelect: (event: SelectChangeEvent<string>) => void;
}

const HotelSelectionStep: React.FC<HotelSelectionStepProps> = ({
  hotelInfo,
  setHotelInfo,
  existingHotels,
  selectedHotelId,
  isNewHotel,
  handleHotelSelect
}) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Hotel
      </Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Select Hotel</InputLabel>
        <Select value={selectedHotelId || 'new'} onChange={handleHotelSelect} label="Select Hotel">
          <MenuItem value="new">Add New Hotel</MenuItem>
          {existingHotels.map((hotel) => (
            <MenuItem key={hotel.id} value={hotel.id}>
              {hotel.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Hotel Info Form */}
      {isNewHotel && (
        <Box component="form" sx={{ mt: 2 }}>
          <TextField fullWidth label="Hotel Name" margin="normal" value={hotelInfo.name} onChange={(e) => setHotelInfo({ ...hotelInfo, name: e.target.value })} required />
          <TextField fullWidth label="Address" margin="normal" value={hotelInfo.address} onChange={(e) => setHotelInfo({ ...hotelInfo, address: e.target.value })} required />
          <TextField fullWidth label="City" margin="normal" value={hotelInfo.city} onChange={(e) => setHotelInfo({ ...hotelInfo, city: e.target.value })} required />
          <TextField fullWidth label="State" margin="normal" value={hotelInfo.state} onChange={(e) => setHotelInfo({ ...hotelInfo, state: e.target.value })} required />
          <TextField fullWidth label="Zip Code" margin="normal" value={hotelInfo.zip_code} onChange={(e) => setHotelInfo({ ...hotelInfo, zip_code: e.target.value })} required />
          <TextField fullWidth label="Country" margin="normal" value={hotelInfo.country} onChange={(e) => setHotelInfo({ ...hotelInfo, country: e.target.value })} required />
          <TextField
            fullWidth
            label="Star Rating"
            margin="normal"
            type="number"
            value={hotelInfo.star_rating}
            onChange={(e) => setHotelInfo({ ...hotelInfo, star_rating: parseInt(e.target.value, 10) || 0 })}
            required
          />
          <TextField
            fullWidth
            label="Number of Rooms"
            margin="normal"
            type="number"
            value={hotelInfo.num_rooms}
            onChange={(e) => setHotelInfo({ ...hotelInfo, num_rooms: parseInt(e.target.value, 10) || 0 })}
            required
          />
          <TextField
            fullWidth
            label="Year Built"
            margin="normal"
            type="number"
            value={hotelInfo.year_built}
            onChange={(e) => setHotelInfo({ ...hotelInfo, year_built: parseInt(e.target.value, 10) || 0 })}
            required
          />
          <TextField
            fullWidth
            label="Lot Size"
            margin="normal"
            type="number"
            value={hotelInfo.lot_size}
            onChange={(e) => setHotelInfo({ ...hotelInfo, lot_size: parseInt(e.target.value, 10) || 0 })}
            required
          />
          <TextField fullWidth label="Description" margin="normal" multiline rows={4} value={hotelInfo.description} onChange={(e) => setHotelInfo({ ...hotelInfo, description: e.target.value })} />
          <TextField fullWidth label="Website" margin="normal" value={hotelInfo.website} onChange={(e) => setHotelInfo({ ...hotelInfo, website: e.target.value })} />
          <TextField fullWidth label="Notes" margin="normal" multiline rows={4} value={hotelInfo.notes} onChange={(e) => setHotelInfo({ ...hotelInfo, notes: e.target.value })} />
          <TextField
            fullWidth
            label="Amenities"
            margin="normal"
            value={hotelInfo.amenities.join(',')}
            onChange={(e) =>
              setHotelInfo({
                ...hotelInfo,
                amenities: e.target.value.split(',').map((a: string) => a.trim()).filter(Boolean),
              })
            }
            helperText="Enter amenities separated by commas"
          />
          <TextField fullWidth label="Property Type" margin="normal" value={hotelInfo.property_type || ''} onChange={(e) => setHotelInfo({ ...hotelInfo, property_type: e.target.value })} />
          <TextField
            fullWidth
            label="Chain Affiliation"
            margin="normal"
            value={hotelInfo.chain_affiliation || ''}
            onChange={(e) => setHotelInfo({ ...hotelInfo, chain_affiliation: e.target.value })}
          />
        </Box>
      )}
    </Box>
  );
};

export default HotelSelectionStep;